import { Field } from "@sitecore-jss/sitecore-jss-react";
import React, { useMemo, useReducer, useState } from "react";
import { useHistory } from "react-router-dom";

import { fetchApplications } from "../../api/application";
import { fetchDictionary } from "../../api/genericData";
import { TextHeader } from "../../components/headers/TextHeader";
import TextInput from "../../components/InputFields/TextInput";
import { Column, Table } from "../../components/table/Table";
import {
  APPLICATION_STATUS_DRAFT,
  APPLICATION_STATUS_SUBMITTED,
  APPLICATION_TYPE_DLP,
  APPLICATION_TYPE_DUP,
} from "../../constants/constants";
import { useDidMount } from "../../hooks/useDidMount";
import { sortDate } from "../../lib/sortUtil";
import styles from "./ApplicationOverview.module.scss";
import { applicationOverviewReducer, initialState } from "./ApplicationOverview.state";

interface TableRowData {
  applicationId: string;
  applicationNumber?: number;
  typeDescription: string;
  statusDescription: string;
  name: string;
  status: string;
  type: string;
  created: string;
  updated?: string;
}

interface Props {
  fields: Record<string, Field<string> & { url: string }>;
}

export const ApplicationOverview = ({ fields }: Props) => {
  const history = useHistory();
  const [data, dispatch] = useReducer(applicationOverviewReducer, initialState);
  const [searchName, setSearchName] = useState("");

  const columns: Column<TableRowData>[] = useMemo(
    () => [
      { Header: fields.DossierNumberLabel.value, accessor: (row) => row.applicationNumber ?? "-", collapse: true },
      { Header: fields.InsuranceLabel.value, accessor: (row) => row.typeDescription, collapse: true },
      { Header: fields.StatusLabel.value, accessor: (row) => row.statusDescription, collapse: true },
      { Header: fields.NameLabel.value, accessor: (row) => row.name },
      { Header: fields.CreatedLabel.value, accessor: (row) => row.created, collapse: true, sortType: sortDate },
      { Header: fields.UpdatedLabel.value, accessor: (row) => row.updated ?? "-", collapse: true, sortType: sortDate },
    ],
    [fields]
  );

  const retrieveApplications = async () => {
    try {
      dispatch({ type: "FETCH_DATA" });

      const { phrases } = await fetchDictionary();
      const fetchedApplications = await fetchApplications();

      dispatch({
        type: "FETCH_SUCCESS",
        payload: { data: { applications: fetchedApplications, dictionary: phrases } },
      });
    } catch {
      dispatch({ type: "FETCH_ERROR" });
    }
  };

  useDidMount(async () => {
    await retrieveApplications();
  });

  const onRowClickHandler = (rowData: TableRowData) => {
    if (rowData.status === APPLICATION_STATUS_DRAFT) {
      if (rowData.type === APPLICATION_TYPE_DUP) {
        const url = `${fields.DupFlowPage.url}?applicationId=${rowData.applicationId}`;
        history.push(url);
      }
      if (rowData.type === APPLICATION_TYPE_DLP) {
        const url = `${fields.DlpFlowPage.url}?applicationId=${rowData.applicationId}`;
        history.push(url);
      }
    }
    if (rowData.status === APPLICATION_STATUS_SUBMITTED) {
      const url = `${fields.DetailsPage.url}?applicationId=${rowData.applicationId}`;
      history.push(url);
    }
  };

  const onChangeSearchName = (value: string) => {
    setSearchName(value);
  };

  const applications = data.applications ?? [];

  const filteredApplications =
    searchName !== ""
      ? applications.filter((a) => a.name.toLowerCase().includes(searchName.toLowerCase()))
      : applications;

  const createNoResultMessage = () => {
    if (data.error) {
      return data.error;
    } else if (data.applications === null) {
      return fields.LoadingMessage.value;
    } else {
      return fields.NoDataFoundMessage.value;
    }
  };

  return (
    <>
      <TextHeader title={fields.Title.value} subtitle={fields.Subtitle.value} />
      <div className={styles.applicationOverview__wrapper}>
        <div className={styles.applicationOverview__search}>
          <TextInput
            fieldData={fields}
            name="SearchOnName"
            fieldName="SearchOnName"
            propertyName="SearchOnName"
            onChange={onChangeSearchName}
            inputValue={searchName}
            disabled={applications.length === 0}
          />
        </div>
        <Table
          columns={columns}
          data={filteredApplications}
          noResultsText={createNoResultMessage()}
          onRowClick={onRowClickHandler}
        />
      </div>
    </>
  );
};
